/** @file Document schema parts: attachments (region, mesh), handles, mounts, skins. */
import { z } from 'zod';
import { prim } from './primitives';

const handle = z.object({ a: prim.px, b: prim.px, c: prim.px.optional() }).strict();
const mount = z.object({ socket: prim.idOf('sock'), fitLength: z.boolean() }).strict();

const region = z
  .object({
    type: z.literal('region'),
    name: prim.name,
    image: prim.idOf('img'),
    color: prim.hex8,
    x: prim.finite,
    y: prim.finite,
    rotation: prim.finite,
    scaleX: prim.finite,
    scaleY: prim.finite,
    width: prim.finite.positive(),
    height: prim.finite.positive(),
    handle: handle.optional(),
    mount: mount.optional(),
  })
  .strict();

const rigidGeometry = z.object({ kind: z.literal('rigid'), vertices: prim.numbers }).strict();
const skinnedGeometry = z
  .object({
    kind: z.literal('skinned'),
    bindVertices: prim.numbers,
    weights: z.array(z.array(z.tuple([prim.idOf('bone'), prim.unit]))),
    /** Bone world affine (6 numbers) at bind time, per weighted bone. */
    bindPose: z.record(prim.idOf('bone'), z.array(prim.finite).length(6)),
  })
  .strict();

const mesh = z
  .object({
    type: z.literal('mesh'),
    name: prim.name,
    image: prim.idOf('img'),
    color: prim.hex8,
    uvs: prim.numbers,
    triangles: z.array(z.number().int().nonnegative()),
    hull: z.number().int().nonnegative(),
    edges: z.array(z.number().int().nonnegative()).optional(),
    geometry: z.discriminatedUnion('kind', [rigidGeometry, skinnedGeometry]),
    mirrorVertexMap: z.array(z.number().int().nonnegative()).optional(),
    handle: handle.optional(),
    mount: mount.optional(),
  })
  .strict();

const skin = z
  .object({
    name: prim.name,
    entries: z.array(z.object({ slot: prim.idOf('slot'), placeholder: prim.name, attachment: prim.idOf('att') }).strict()),
  })
  .strict();

export const attachmentParts = { attachment: z.discriminatedUnion('type', [region, mesh]), skin, handle, mount };

export type Handle = z.infer<typeof handle>;
export type Mount = z.infer<typeof mount>;
export type RegionAtt = z.infer<typeof region>;
export type MeshAtt = z.infer<typeof mesh>;
export type AttachmentDoc = RegionAtt | MeshAtt;
export type SkinDoc = z.infer<typeof skin>;
